/**
 * The theme, and the only thing `localStorage` holds.
 *
 * It is per-device, it can throw, and it is not a place for anything that matters. The hook also
 * follows `prefers-color-scheme` while no explicit choice is stored, which the prototype does not.
 */

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'theme';

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function current(): Theme {
  const attribute = document.documentElement.dataset.theme;
  return attribute === 'dark' ? 'dark' : 'light';
}

export interface ThemeState {
  theme: Theme;
  toggle: () => void;
  set: (theme: Theme) => void;
}

export function useTheme(): ThemeState {
  const [theme, setThemeState] = useState<Theme>(current);

  const set = useCallback((next: Theme) => {
    document.documentElement.dataset.theme = next;
    setThemeState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // A privacy mode that refuses storage still gets the theme it asked for this session.
    }
  }, []);

  const toggle = useCallback(() => {
    set(current() === 'dark' ? 'light' : 'dark');
  }, [set]);

  useEffect(() => {
    if (stored() !== null) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const follow = (event: MediaQueryListEvent) => {
      const next: Theme = event.matches ? 'dark' : 'light';
      document.documentElement.dataset.theme = next;
      setThemeState(next);
    };
    query.addEventListener('change', follow);
    return () => query.removeEventListener('change', follow);
  }, []);

  return { theme, toggle, set };
}
